// v2 data layer: one provider owns the websocket connection and all message
// handling; pages consume slices via useDeploymentData().
//
// Key structural difference from the legacy Dashboard: historical data lives
// in its own per-service map and NEVER replaces the live record list, so
// real-time updates keep flowing no matter which page is open.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
  ArtifactData,
  DeploymentLogs,
  DeploymentRecord,
  EnvironmentInfo,
  JobDefinition,
  LockInfo,
  LogEntry,
  LogSource,
  ModuleVersionsEntry,
  RcaFeedbackInput,
  RcaStartResult,
  RollbackBreachItem,
  RollbackDoc,
  RollbackDocKind,
  RollbackProfileEntry,
  RollbackWatchItem,
  TerraformStack,
  WebSocketMessage,
} from '../../types/DeploymentRecord';
import { reassembleLogEntries } from '../lib/logs';
import { rcaFeedbackMessage } from '../lib/rca';
import { profileVersionKey, rollbackProfilesKey } from '../lib/rollbackProfiles';
import { WATCHES_PAGE_SIZE, assembleDocPieces, mergePage, parseRollbackDoc, rollbackListKey } from '../lib/rollbackWatches';
import { mergeStackPieces, stackModulesKey } from '../lib/stackModules';
import { ConnectionStatus, WebSocketService } from '../../services/WebSocketService';
import {
  appendNewRecords,
  applyDataChange,
  artifactKey,
  dataChangeRecord,
  foldStrayAppsIntoNamesakes,
  historicalKey,
  planFallbackApplies,
  processRecords,
  serviceFilterKey,
  upsertRecord,
} from '../lib/records';
import { lockMapKey, recordLockKey } from '../lib/locks';
import { staleCurrentIds } from '../lib/grouping';

/** How long a row stays highlighted after a live update touches it. */
const FLASH_TTL_MS = 2_500;

/** Per-record artifact button state for the github-commit link. */
export type ArtifactStatus = 'idle' | 'loading' | 'ready' | 'none' | 'error';

/** Give up on an artifact lookup after this long (no correlation id to match). */
const ARTIFACT_TIMEOUT_MS = 10_000;

/** Give up on a logs request after this long. */
const LOGS_TIMEOUT_MS = 15_000;

/**
 * Give up waiting for a startRca or submitRcaFeedback reply after this long.
 * The Lambda's own apiserver calls time out at 20 s, so a slow cluster still
 * answers first.
 */
const RCA_TIMEOUT_MS = 30_000;

/** A startRca request awaiting its rcaStarted (or error) frame. */
interface PendingRcaRequest {
  resolve: (result: RcaStartResult) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** A submitRcaFeedback request awaiting its rcaFeedbackSaved (or error) frame. */
interface PendingRcaFeedbackRequest {
  resolve: () => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** Optional bounds on one getLogs request. */
export interface FetchLogsOptions {
  /** One task by name, or 'failed', or 'all' (the default). */
  task?: string;
  /** Keep the last N lines of each task; 0 asks for the whole log. */
  tail?: number;
}

/** A pending fetchLogs promise awaiting its response stream to complete. */
interface PendingLogsRequest {
  resolve: (logs: DeploymentLogs) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** Header fields a getLogs response repeats on every chunk. */
interface LogsResponseMeta {
  workflowName: string;
  engine: string;
  source: LogSource;
  deploymentComplete: boolean;
  truncated: boolean;
  cursor: string;
}

export interface HistoricalSlice {
  records: DeploymentRecord[];
  loading: boolean;
}

/** One profile version fetched for the version picker. */
export interface ProfileVersionState {
  entry?: RollbackProfileEntry;
  loading: boolean;
  error?: string;
}

/** A job's rollback evaluation profiles, keyed by rollbackProfilesKey. */
export interface RollbackProfilesSlice {
  /** Every env_region with a profile, each at its current version. */
  entries: RollbackProfileEntry[];
  loading: boolean;
  loaded: boolean;
  error?: string;
  /** Versions by profileVersionKey(env_region, version), current ones included. */
  versions: Record<string, ProfileVersionState>;
}

/** A job's watches or breaches under one env filter, keyed by rollbackListKey. */
export interface RollbackListSlice<T> {
  /** Newest first; older pages appended. */
  items: T[];
  loading: boolean;
  loaded: boolean;
  error?: string;
  /** Cursor for the next older page; absent when there is none. */
  nextBefore?: string;
}

export interface RollbackListOptions {
  envRegion?: string;
  before?: string;
}

/** A case or breach report, keyed by its S3 key. */
export interface RollbackDocState {
  doc?: RollbackDoc;
  loading: boolean;
  error?: string;
}

/** A terraform job's module inventory, keyed by stackModulesKey. */
export interface StackModulesSlice {
  /** One per env_region, split pieces already joined. */
  stacks: TerraformStack[];
  loading: boolean;
  loaded: boolean;
  error?: string;
}

/** How long getModuleVersions may go unanswered before the page falls back. */
export const MODULE_VERSIONS_TIMEOUT_MS = 30_000;

export interface DeploymentDataValue {
  // Live current-deployment records (parsed; includes dynamic-env records)
  records: DeploymentRecord[];
  /** Records without a TTL — long-lived environments. */
  staticRecords: DeploymentRecord[];
  /** Records with a TTL — dynamic (ephemeral) environments. */
  dynamicRecords: DeploymentRecord[];
  /**
   * `staticRecords` with stale/superseded failed & stuck-deploying records
   * dropped. Use this for current-state surfaces (Deploying Now, env matrix,
   * Needs-attention) so old/failed deploys don't linger. `success`/`pending`
   * records are never removed, so deployed state is preserved.
   */
  currentRecords: DeploymentRecord[];
  /** Ids dropped from {@link currentRecords} (over the full live record list). */
  staleCurrentIds: Set<string>;
  wsStatus: ConnectionStatus;
  loading: boolean;
  loadingProgress: { current: number; total: number } | null;
  error: string | null;
  clearError: () => void;
  reconnect: () => void;
  refresh: () => void;

  // Historical (per service, keyed by `${app}:${service}`)
  fetchHistorical: (appName: string, service: string, limit?: number) => void;
  historical: Map<string, HistoricalSlice>;

  // Deployment logs (fetched on demand; responses carry deployment_id so
  // overlapping requests correlate). Resolves once the chunk stream completes,
  // rejects on a backend error or timeout.
  fetchLogs: (deploymentId: string, opts?: FetchLogsOptions) => Promise<DeploymentLogs>;

  // Root-cause analysis: asks start_rca to submit the pipeline-rca workflow.
  // Resolves with the run (new, or the one already in flight); rejects with the
  // Lambda's refusal (not failed, not Argo, …) or on timeout. The analysis
  // itself lands on the record's `rca` map over the live stream.
  startRca: (deploymentId: string) => Promise<RcaStartResult>;

  // Rates a finished analysis through start_rca. Resolves once the
  // verdict is stored; rejects with the Lambda's refusal (run not done, or
  // replaced by a newer one) or on timeout. The stored verdict comes back on
  // the record's rca.feedback over the live stream.
  submitRcaFeedback: (input: RcaFeedbackInput) => Promise<void>;

  // Rollback evaluation profiles per job (getRollbackProfiles), keyed by
  // rollbackProfilesKey(app, jobId). fetchRollbackProfileVersion fills the
  // slice's `versions` cache and is a no-op for a version already held.
  rollbackProfiles: Map<string, RollbackProfilesSlice>;
  fetchRollbackProfiles: (appName: string, jobId: string) => void;
  fetchRollbackProfileVersion: (appName: string, jobId: string, envRegion: string, version: number) => void;

  // Terraform module inventory per job (getStackModules), keyed by
  // stackModulesKey(app, jobId).
  stackModules: Map<string, StackModulesSlice>;
  fetchStackModules: (appName: string, jobId: string) => void;
  // Published versions per registry module source (getModuleVersions, the
  // Spacelift registry), keyed by the source as sent.
  moduleVersions: Map<string, ModuleVersionsEntry>;
  /** Requests sent and not yet answered. */
  moduleVersionsPending: number;
  /** The last request's error frame, e.g. the registry isn't configured. */
  moduleVersionsError?: string;
  fetchModuleVersions: (sources: string[]) => void;

  // Auto-rollback watches and breaches per job and env filter
  // (getRollbackWatches, getRollbackBreaches), keyed by rollbackListKey. A
  // `before` fetch appends the older page; any other replaces the list.
  rollbackWatches: Map<string, RollbackListSlice<RollbackWatchItem>>;
  fetchRollbackWatches: (appName: string, jobId: string, opts?: RollbackListOptions) => void;
  rollbackBreaches: Map<string, RollbackListSlice<RollbackBreachItem>>;
  fetchRollbackBreaches: (appName: string, jobId: string, opts?: RollbackListOptions) => void;
  // Cases and breach reports (getRollbackDoc), keyed by S3 key; a no-op for a
  // doc already held or in flight.
  rollbackDocs: Map<string, RollbackDocState>;
  fetchRollbackDoc: (appName: string, jobId: string, kind: RollbackDocKind, key: string) => void;

  // Jobs catalog
  jobs: JobDefinition[];
  jobsLoading: boolean;
  jobsLoaded: boolean;
  fetchJobs: () => void;

  // Environment info (account/fqdn metadata)
  environmentInfo: EnvironmentInfo[];
  environmentInfoLoaded: boolean;
  fetchEnvironmentInfo: () => void;

  // Per-record artifact cache for the github-commit button (fetched on click)
  fetchArtifactFor: (record: DeploymentRecord) => void;
  githubLinkFor: (record: DeploymentRecord) => string | undefined;
  artifactStatusFor: (record: DeploymentRecord) => ArtifactStatus;

  // Unique value pools for search/filters
  uniqueApps: string[];
  uniqueServices: string[];
  uniqueEnvs: string[];
  uniqueTypes: string[];
  uniqueBuildUsers: string[];
}

const DeploymentDataContext = createContext<DeploymentDataValue | null>(null);

export const useDeploymentData = (): DeploymentDataValue => {
  const ctx = useContext(DeploymentDataContext);
  if (!ctx) {
    throw new Error('useDeploymentData must be used within DeploymentDataProvider');
  }
  return ctx;
};

// Set of record ids touched by a live update in the last FLASH_TTL_MS. Kept in
// its own context so rows subscribing for the flash highlight don't re-render on
// every records change — only when the flash set itself changes.
const FlashedKeysContext = createContext<Set<string>>(new Set());

/** True while the given record id is freshly changed by a live update. */
export const useRowFlash = (id?: string): boolean => {
  const set = useContext(FlashedKeysContext);
  return !!id && set.has(id);
};

// Active job locks keyed by `${job_key}::${env_name}`. Its own context so rows
// reading their lock don't re-render on every records change — only when a lock
// actually changes. (DEVOPS-1633)
const LocksContext = createContext<Map<string, LockInfo>>(new Map());

/** The active lock for a deployment row, or undefined if none. */
export const useLockFor = (record: DeploymentRecord): LockInfo | undefined => {
  const locks = useContext(LocksContext);
  if (locks.size === 0) return undefined;
  const key = recordLockKey(record);
  return key ? locks.get(key) : undefined;
};

/** The full active-locks map (keyed `${job_key}::${env_name}`). For callers
 *  that need to look up locks for several records without a per-record hook. */
export const useLocks = (): Map<string, LockInfo> => useContext(LocksContext);

// Most-recent s3_plan_link per (app, jobId, type, env), across all live
// records — generalizes ServicePage's per-service planLinkByEnv. A terraform
// record's own s3_plan_link is often empty while the run is in flight: the
// link can land on a sibling record for the same service+env, or only after
// `tf scan` publishes it. Its own context, like LocksContext, so rows don't
// re-render for unrelated record changes.
export interface PlanLinkInfo {
  link: string;
  ts: number; // epoch ms of the plan-bearing record; consumers gate on recency
}

const PlanLinksContext = createContext<Map<string, PlanLinkInfo>>(new Map());

const planLinkKey = (app: string, jobId: string, type: string, env: string): string =>
  `${app}:${jobId}:${type.toLowerCase()}:${env}`;

/** The most recent plan link for this record's service+env, wherever it landed. */
export const usePlanLinkFor = (record: DeploymentRecord): PlanLinkInfo | undefined => {
  const links = useContext(PlanLinksContext);
  const jobId = record.serviceName || record.job_id || '';
  if (!record.app || !jobId) return undefined;
  const env = record.envName || record.env || '';
  const info = links.get(planLinkKey(record.app, jobId, record.type || '', env));
  if (!info || !planFallbackApplies(info.ts, record)) return undefined;
  return info;
};

const WS_URL = process.env.REACT_APP_WS_URL || 'wss://your-api-gateway-url/stage';

/** Build the getArtifactData payload for a record (version omitted for terraform). */
const buildArtifactMessage = (record: DeploymentRecord): Record<string, any> | null => {
  const jobId = record.job_id || record.service?.split('/').pop() || '';
  const appName = record.app || record.app_name || '';
  if (!appName || !jobId) return null;
  const message: Record<string, any> = {
    action: 'getArtifactData',
    app_name: appName,
    job_id: jobId,
    type: record.type || 'lambda',
  };
  if ((record.type || '').toLowerCase() !== 'terraform') {
    message.version = record.version;
  }
  return message;
};

export const DeploymentDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { oktaAuth } = useOktaAuth();

  // Raw, as-received records (parsed by processRecord, incrementally
  // upserted by the websocket handlers below). Never read directly by
  // consumers — `records` (derived below) is what's exposed.
  const [rawRecords, setRawRecords] = useState<DeploymentRecord[]>([]);
  const [wsStatus, setWsStatus] = useState<ConnectionStatus>('disconnected');
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] =
    useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live-update flash highlight: ids touched by a recent data_change.
  const [flashedKeys, setFlashedKeys] = useState<Set<string>>(new Set());
  const flashTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const markFlashed = useCallback((id: string) => {
    setFlashedKeys(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const timers = flashTimersRef.current;
    const existing = timers.get(id);
    if (existing) clearTimeout(existing);
    timers.set(
      id,
      setTimeout(() => {
        timers.delete(id);
        setFlashedKeys(prev => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, FLASH_TTL_MS)
    );
  }, []);

  // Ids whose data_change actually applied (queued from the setRawRecords
  // updater), flashed once the new records commit.
  const pendingFlashRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const pending = pendingFlashRef.current;
    if (pending.size === 0) return;
    pending.forEach(id => markFlashed(id));
    pending.clear();
  }, [rawRecords, markFlashed]);

  // Clear any pending flash timers on unmount.
  useEffect(() => {
    const timers = flashTimersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const [historical, setHistorical] = useState<Map<string, HistoricalSlice>>(new Map());
  const historicalChunksRef = useRef<Map<string, any[]>>(new Map());

  const [jobs, setJobs] = useState<JobDefinition[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const jobsChunksRef = useRef<any[]>([]);

  const [rollbackProfiles, setRollbackProfiles] = useState<Map<string, RollbackProfilesSlice>>(new Map());
  // List-reply chunks keyed by `${rollbackProfilesKey}::${timestamp}`.
  const rollbackProfileChunksRef = useRef<Map<string, RollbackProfileEntry[]>>(new Map());
  // Read inside fetchRollbackProfileVersion's dedupe without making it depend
  // on (and re-create with) the state.
  const rollbackProfilesRef = useRef(rollbackProfiles);
  rollbackProfilesRef.current = rollbackProfiles;

  const [stackModules, setStackModules] = useState<Map<string, StackModulesSlice>>(new Map());
  // Reply chunks keyed by `${stackModulesKey}::${timestamp}`.
  const stackModuleChunksRef = useRef<Map<string, TerraformStack[]>>(new Map());
  const [moduleVersions, setModuleVersions] = useState<Map<string, ModuleVersionsEntry>>(new Map());
  const [moduleVersionsPending, setModuleVersionsPending] = useState(0);
  // Mirrors moduleVersionsPending so the timeout can read it outside render.
  const moduleVersionsPendingRef = useRef(0);
  const settleModuleVersions = useCallback((n: number) => {
    moduleVersionsPendingRef.current = Math.max(0, n);
    setModuleVersionsPending(moduleVersionsPendingRef.current);
  }, []);
  const [moduleVersionsError, setModuleVersionsError] = useState<string | undefined>();
  // Reply chunks keyed by the per-request timestamp.
  const moduleVersionChunksRef = useRef<Map<string, ModuleVersionsEntry[]>>(new Map());

  const [rollbackWatches, setRollbackWatches] = useState<Map<string, RollbackListSlice<RollbackWatchItem>>>(
    new Map()
  );
  const [rollbackBreaches, setRollbackBreaches] = useState<Map<string, RollbackListSlice<RollbackBreachItem>>>(
    new Map()
  );
  // Reply chunks of both lists, keyed by `${action}::${rollbackListKey}::${timestamp}`.
  const rollbackListChunksRef = useRef<Map<string, any[]>>(new Map());
  const [rollbackDocs, setRollbackDocs] = useState<Map<string, RollbackDocState>>(new Map());
  // A doc's JSON arrives as string pieces, keyed by `${key}::${timestamp}`.
  const rollbackDocChunksRef = useRef<Map<string, Array<{ chunk?: number; data: string }>>>(new Map());
  const rollbackDocsRef = useRef(rollbackDocs);
  rollbackDocsRef.current = rollbackDocs;

  const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo[]>([]);
  const [environmentInfoLoaded, setEnvironmentInfoLoaded] = useState(false);
  const environmentInfoRequestedRef = useRef(false);

  // Active job locks keyed by `${job_key}::${env_name}`.
  const [locks, setLocks] = useState<Map<string, LockInfo>>(new Map());
  const locksChunksRef = useRef<LockInfo[]>([]);

  // Artifact cache keyed by artifactKey(record). `null` = looked up, no artifact.
  const [artifactCache, setArtifactCache] = useState<Map<string, ArtifactData | null>>(new Map());
  const [artifactLoadingKeys, setArtifactLoadingKeys] = useState<Set<string>>(new Set());
  // Keys whose lookup timed out — surfaced as a retryable "Unable to fetch".
  const [artifactErrorKeys, setArtifactErrorKeys] = useState<Set<string>>(new Set());
  // Sequential fetch plumbing (responses carry no correlation id → FIFO).
  const artifactQueueRef = useRef<string[]>([]);
  const artifactRecordsRef = useRef<Map<string, DeploymentRecord>>(new Map());
  const artifactInFlightRef = useRef<string | null>(null);
  const artifactTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Deployment logs request/response plumbing. Responses carry the
  // deployment_id, plus a per-request `timestamp` id the backend stamps on
  // every chunk, so overlapping requests stay separable:
  //   - pending promises are queued per deployment_id (FIFO within an id, so
  //     two in-flight polls for the same deployment resolve in send order)
  //   - chunk buffers + metadata are keyed by `${deployment_id}::${timestamp}`
  //     so interleaved chunk streams for the same deployment don't merge
  const logsPendingRef = useRef<Map<string, PendingLogsRequest[]>>(new Map());
  const logsChunksRef = useRef<Map<string, LogEntry[]>>(new Map());
  const logsMetaRef = useRef<Map<string, LogsResponseMeta>>(new Map());

  // startRca waiters, one per deployment id: replies carry the deployment_id
  // and nothing else to correlate by.
  const rcaPendingRef = useRef<Map<string, PendingRcaRequest>>(new Map());
  // submitRcaFeedback waiters, keyed the same way and for the same reason.
  const rcaFeedbackPendingRef = useRef<Map<string, PendingRcaFeedbackRequest>>(new Map());

  const wsRef = useRef<WebSocketService | null>(null);

  // Pop the oldest pending request for a deployment id (FIFO), cleaning up the
  // queue entry. Returns undefined if none is waiting.
  const takePendingLogs = useCallback((deploymentId: string): PendingLogsRequest | undefined => {
    const queue = logsPendingRef.current.get(deploymentId);
    if (!queue || queue.length === 0) return undefined;
    const pending = queue.shift();
    if (queue.length === 0) logsPendingRef.current.delete(deploymentId);
    return pending;
  }, []);

  // Send the next queued artifact request if nothing is in flight.
  const pumpArtifacts = useCallback(() => {
    if (artifactInFlightRef.current !== null) return;
    const key = artifactQueueRef.current.shift();
    if (!key) return;
    const record = artifactRecordsRef.current.get(key);
    const message = record ? buildArtifactMessage(record) : null;
    if (!message || !wsRef.current?.isConnected()) {
      // Can't send — surface as an "unable to fetch" so the button doesn't hang.
      setArtifactLoadingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setArtifactErrorKeys(prev => new Set(prev).add(key));
      pumpArtifacts();
      return;
    }
    artifactInFlightRef.current = key;
    wsRef.current.sendMessage(message);
    // No correlation id, so give up after a timeout and let the user retry.
    artifactTimeoutRef.current = setTimeout(() => {
      if (artifactInFlightRef.current !== key) return; // already resolved
      artifactInFlightRef.current = null;
      artifactTimeoutRef.current = null;
      setArtifactLoadingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setArtifactErrorKeys(prev => new Set(prev).add(key));
      pumpArtifacts();
    }, ARTIFACT_TIMEOUT_MS);
  }, []);

  const handleMessage = useCallback((message: WebSocketMessage) => {
    if (message.action === 'getCurrent' && message.data) {
      const processed = processRecords(message.data);
      const isChunked = message.chunk !== undefined;

      if (isChunked) {
        setLoadingProgress({ current: message.chunk || 0, total: message.totalChunks || 0 });
        setRawRecords(prev => appendNewRecords(prev, processed));
        if (message.isComplete) {
          setLoading(false);
          setLoadingProgress(null);
        }
      } else {
        setRawRecords(processed);
        setLoading(false);
      }
      return;
    }

    if (message.action === 'data_change') {
      // Process the record once — shared by the live-list apply and the flash
      // highlight. Null for REMOVE (rows disappear, so only upserts flash).
      const rec = dataChangeRecord(message);
      if (!rec) {
        setRawRecords(prev => applyDataChange(prev, message));
        return;
      }
      setRawRecords(prev => {
        const next = upsertRecord(prev, rec);
        // Queue the flash only if the update actually applied — an
        // out-of-order (stale-sequence) update returns `prev` and must not
        // flash. Adding to a Set is idempotent, so a re-run updater (StrictMode,
        // interrupted render) is harmless; the effect below drains it.
        if (next !== prev && rec.id) pendingFlashRef.current.add(rec.id);
        return next;
      });
      return;
    }

    if (message.action === 'getHistorical' && message.data) {
      const key = historicalKey(message.app_name || '', message.service || '');
      const isChunked = message.chunk !== undefined;

      if (isChunked) {
        const chunks = historicalChunksRef.current;
        chunks.set(key, [...(chunks.get(key) || []), ...message.data]);
        if (message.isComplete) {
          const all = chunks.get(key) || [];
          chunks.delete(key);
          const processed = processRecords(all);
          setHistorical(prev => {
            const next = new Map(prev);
            next.set(key, { records: processed, loading: false });
            return next;
          });
        }
      } else {
        const processed = processRecords(message.data);
        setHistorical(prev => {
          const next = new Map(prev);
          next.set(key, { records: processed, loading: false });
          return next;
        });
      }
      return;
    }

    if (message.action === 'getJobs') {
      const isChunked = message.chunk !== undefined;
      if (isChunked) {
        if (Array.isArray(message.data)) {
          jobsChunksRef.current = [...jobsChunksRef.current, ...message.data];
        }
        if (message.isComplete) {
          setJobs(jobsChunksRef.current);
          setJobsLoading(false);
          setJobsLoaded(true);
          jobsChunksRef.current = [];
        }
      } else {
        if (Array.isArray(message.data)) setJobs(message.data);
        setJobsLoading(false);
        setJobsLoaded(true);
      }
      return;
    }

    if (message.action === 'getRollbackProfiles') {
      const key = rollbackProfilesKey(message.app_name || '', message.job_id || '');
      const update = (fn: (slice: RollbackProfilesSlice) => RollbackProfilesSlice) =>
        setRollbackProfiles(prev => {
          const next = new Map(prev);
          next.set(key, fn(prev.get(key) || { entries: [], loading: false, loaded: false, versions: {} }));
          return next;
        });

      // A reply naming env_region answers the version picker, not the list.
      if (message.env_region) {
        const envRegion = message.env_region;
        const entry = message.data as RollbackProfileEntry | undefined;
        const asked = profileVersionKey(envRegion, message.version || 0);
        update(slice => {
          const versions = { ...slice.versions };
          if (message.error || !entry) {
            versions[asked] = { loading: false, error: message.error || 'No profile returned' };
          } else {
            delete versions[asked];
            versions[profileVersionKey(envRegion, entry.profile?.version ?? message.version ?? 0)] = {
              entry,
              loading: false,
            };
          }
          return { ...slice, versions };
        });
        return;
      }

      if (message.error) {
        update(slice => ({ ...slice, loading: false, loaded: true, error: message.error }));
        return;
      }
      const chunkKey = `${key}::${message.timestamp || ''}`;
      const chunks = rollbackProfileChunksRef.current;
      const all = [...(chunks.get(chunkKey) || []), ...(Array.isArray(message.data) ? message.data : [])];
      if (message.chunk !== undefined && !message.isComplete) {
        chunks.set(chunkKey, all);
        return;
      }
      chunks.delete(chunkKey);
      update(slice => {
        const versions = { ...slice.versions };
        all.forEach(e => {
          versions[profileVersionKey(e.env_region, e.pointer.version)] = { entry: e, loading: false };
        });
        return { entries: all, loading: false, loaded: true, versions };
      });
      return;
    }

    if (message.action === 'getRollbackWatches' || message.action === 'getRollbackBreaches') {
      const setList: React.Dispatch<React.SetStateAction<Map<string, RollbackListSlice<any>>>> =
        message.action === 'getRollbackWatches' ? setRollbackWatches : setRollbackBreaches;
      const key = rollbackListKey(message.app_name || '', message.job_id || '', message.env_region);
      const older = !!message.before;
      const update = (fn: (slice: RollbackListSlice<any>) => RollbackListSlice<any>) =>
        setList(prev => {
          const next = new Map(prev);
          next.set(key, fn(prev.get(key) || { items: [], loading: false, loaded: false }));
          return next;
        });
      if (message.error) {
        // A failed older page keeps the rows already shown.
        update(slice => ({ ...slice, loading: false, loaded: true, error: message.error }));
        return;
      }
      const chunkKey = `${message.action}::${key}::${message.timestamp || ''}`;
      const chunks = rollbackListChunksRef.current;
      const all = [...(chunks.get(chunkKey) || []), ...(Array.isArray(message.data) ? message.data : [])];
      if (message.chunk !== undefined && !message.isComplete) {
        chunks.set(chunkKey, all);
        return;
      }
      chunks.delete(chunkKey);
      update(slice => ({
        items: mergePage(slice.items, all, older),
        loading: false,
        loaded: true,
        nextBefore: message.next_before || undefined,
      }));
      return;
    }

    if (message.action === 'getRollbackDoc') {
      const docKey = message.key || '';
      const settle = (state: RollbackDocState) =>
        setRollbackDocs(prev => {
          const next = new Map(prev);
          next.set(docKey, state);
          return next;
        });
      if (message.error) {
        settle({ loading: false, error: message.error });
        return;
      }
      const chunkKey = `${docKey}::${message.timestamp || ''}`;
      const chunks = rollbackDocChunksRef.current;
      const pieces = [
        ...(chunks.get(chunkKey) || []),
        { chunk: message.chunk, data: typeof message.data === 'string' ? message.data : '' },
      ];
      if (message.chunk !== undefined && !message.isComplete) {
        chunks.set(chunkKey, pieces);
        return;
      }
      chunks.delete(chunkKey);
      try {
        settle({ doc: parseRollbackDoc(assembleDocPieces(pieces)), loading: false });
      } catch {
        settle({ loading: false, error: 'The document could not be read' });
      }
      return;
    }

    if (message.action === 'getStackModules') {
      const key = stackModulesKey(message.app_name || '', message.job_id || '');
      const settle = (slice: StackModulesSlice) =>
        setStackModules(prev => {
          const next = new Map(prev);
          next.set(key, slice);
          return next;
        });
      if (message.error) {
        settle({ stacks: [], loading: false, loaded: true, error: message.error });
        return;
      }
      const chunkKey = `${key}::${message.timestamp || ''}`;
      const chunks = stackModuleChunksRef.current;
      const all = [...(chunks.get(chunkKey) || []), ...(Array.isArray(message.data) ? message.data : [])];
      if (message.chunk !== undefined && !message.isComplete) {
        chunks.set(chunkKey, all);
        return;
      }
      chunks.delete(chunkKey);
      settle({ stacks: mergeStackPieces(all), loading: false, loaded: true });
      return;
    }

    if (message.action === 'getModuleVersions') {
      if (message.error) {
        setModuleVersionsError(message.error);
        settleModuleVersions(moduleVersionsPendingRef.current - 1);
        return;
      }
      const chunkKey = message.timestamp || '';
      const chunks = moduleVersionChunksRef.current;
      const all = [...(chunks.get(chunkKey) || []), ...(Array.isArray(message.data) ? message.data : [])];
      if (message.chunk !== undefined && !message.isComplete) {
        chunks.set(chunkKey, all);
        return;
      }
      chunks.delete(chunkKey);
      setModuleVersionsError(undefined);
      settleModuleVersions(moduleVersionsPendingRef.current - 1);
      setModuleVersions(prev => {
        const next = new Map(prev);
        all.forEach((e: ModuleVersionsEntry) => next.set(e.source, e));
        return next;
      });
      return;
    }

    if (message.action === 'getEnvironmentInfo') {
      if (Array.isArray(message.data)) setEnvironmentInfo(message.data);
      setEnvironmentInfoLoaded(true);
      return;
    }

    if (message.action === 'getLocks') {
      const buildMap = (list: LockInfo[]) => {
        const next = new Map<string, LockInfo>();
        list.forEach(l => {
          if (l && l.job_key && l.env_name) next.set(lockMapKey(l.job_key, l.env_name), l);
        });
        return next;
      };
      const isChunked = message.chunk !== undefined;
      if (isChunked) {
        if (Array.isArray(message.data)) {
          locksChunksRef.current = [...locksChunksRef.current, ...message.data];
        }
        if (message.isComplete) {
          setLocks(buildMap(locksChunksRef.current));
          locksChunksRef.current = [];
        }
      } else if (Array.isArray(message.data)) {
        setLocks(buildMap(message.data));
      }
      return;
    }

    if (message.action === 'lock_change') {
      setLocks(prev => {
        const next = new Map(prev);
        if (message.changeType === 'REMOVE' && message.oldData) {
          const { job_key, env_name } = message.oldData;
          if (job_key && env_name) next.delete(lockMapKey(job_key, env_name));
        } else if (message.newData) {
          const lock = message.newData as LockInfo;
          if (lock.job_key && lock.env_name) {
            next.set(lockMapKey(lock.job_key, lock.env_name), lock);
          }
        }
        return next;
      });
      return;
    }

    if (message.action === 'getArtifactData') {
      const key = artifactInFlightRef.current;
      artifactInFlightRef.current = null;
      if (artifactTimeoutRef.current) {
        clearTimeout(artifactTimeoutRef.current);
        artifactTimeoutRef.current = null;
      }
      if (key) {
        const data = message.found && message.data ? message.data : null;
        setArtifactCache(prev => new Map(prev).set(key, data));
        setArtifactLoadingKeys(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
      pumpArtifacts();
      return;
    }

    if (message.action === 'startRca') {
      const deploymentId = message.deployment_id || '';
      const pending = rcaPendingRef.current.get(deploymentId);
      if (!pending) return;
      rcaPendingRef.current.delete(deploymentId);
      clearTimeout(pending.timer);
      if (message.error) {
        pending.reject(new Error(message.error));
      } else {
        pending.resolve({
          deploymentId,
          workflow: message.workflow || '',
          status: message.status === 'already_running' ? 'already_running' : 'submitted',
        });
      }
      return;
    }

    if (message.action === 'submitRcaFeedback') {
      const deploymentId = message.deployment_id || '';
      const pending = rcaFeedbackPendingRef.current.get(deploymentId);
      if (!pending) return;
      rcaFeedbackPendingRef.current.delete(deploymentId);
      clearTimeout(pending.timer);
      if (message.error) {
        pending.reject(new Error(message.error));
      } else {
        pending.resolve();
      }
      return;
    }

    if (message.action === 'getLogs') {
      const deploymentId = message.deployment_id || '';

      // Error envelope: fail the oldest waiter for this deployment.
      if (message.error) {
        const pending = takePendingLogs(deploymentId);
        if (pending) {
          clearTimeout(pending.timer);
          pending.reject(new Error(message.error));
        }
        return;
      }

      // Accumulate this response's chunks under deployment_id + request id
      // (`timestamp`) so a second in-flight poll for the same deployment can't
      // bleed into this one.
      const reqId = message.timestamp || '';
      const key = `${deploymentId}::${reqId}`;
      const isChunked = message.chunk !== undefined;
      const entries: LogEntry[] = Array.isArray(message.data) ? message.data : [];

      if (isChunked) {
        logsChunksRef.current.set(key, [...(logsChunksRef.current.get(key) || []), ...entries]);
      } else {
        logsChunksRef.current.set(key, entries);
      }
      logsMetaRef.current.set(key, {
        workflowName: message.workflow_name || '',
        engine: message.engine || '',
        source: (message.source as LogSource) || 'none',
        deploymentComplete: !!message.deploymentComplete,
        truncated: !!message.truncated,
        cursor: message.cursor || '',
      });

      const complete = !isChunked || !!message.isComplete;
      if (complete) {
        const all = logsChunksRef.current.get(key) || [];
        const meta = logsMetaRef.current.get(key);
        logsChunksRef.current.delete(key);
        logsMetaRef.current.delete(key);
        const pending = takePendingLogs(deploymentId);
        if (pending) {
          clearTimeout(pending.timer);
          pending.resolve({
            deploymentId,
            workflowName: meta?.workflowName || '',
            engine: meta?.engine || '',
            source: meta?.source || 'none',
            deploymentComplete: !!meta?.deploymentComplete,
            truncated: !!meta?.truncated,
            cursor: meta?.cursor || '',
            tasks: reassembleLogEntries(all),
          });
        }
      }
    }
  }, [pumpArtifacts, takePendingLogs, settleModuleVersions]);

  // Connect once on mount. WebSocketService sends getCurrent automatically.
  useEffect(() => {
    const ws = new WebSocketService({
      url: WS_URL,
      oktaAuth,
      onMessage: handleMessage,
      onStatusChange: setWsStatus,
      onError: () => setError('WebSocket connection error'),
    });
    wsRef.current = ws;
    setRawRecords([]);
    setLoading(true);
    setLoadingProgress(null);
    ws.connect();
    return () => {
      ws.disconnect();
      wsRef.current = null;
    };
  }, [oktaAuth, handleMessage]);

  // Auto-clear the error banner once we're reconnected.
  useEffect(() => {
    if (wsStatus === 'connected') setError(null);
  }, [wsStatus]);

  // Pull a fresh lock snapshot whenever the socket (re)connects; lock_change
  // events keep it live thereafter. Locks are needed wherever rows render, so
  // (unlike jobs/env info) this fetches eagerly rather than on demand.
  useEffect(() => {
    if (wsStatus === 'connected') {
      // Clear any partial chunks from a prior (possibly cut-off) cycle so a new
      // snapshot can't merge stale leftovers into ghost locks.
      locksChunksRef.current = [];
      wsRef.current?.sendMessage({ action: 'getLocks' });
    }
  }, [wsStatus]);

  // Allow environment info to be re-requested after a drop. The request gate
  // is set optimistically before send, so without this a single transient
  // disconnect/error would permanently block retries in the same session.
  useEffect(() => {
    if (wsStatus !== 'connected' && !environmentInfoLoaded) {
      environmentInfoRequestedRef.current = false;
    }
  }, [wsStatus, environmentInfoLoaded]);

  const reconnect = useCallback(() => {
    wsRef.current?.connect();
  }, []);

  const refresh = useCallback(() => {
    if (wsRef.current?.isConnected()) {
      setRawRecords([]);
      setLoading(true);
      setLoadingProgress(null);
      wsRef.current.sendMessage({ action: 'getCurrent' });
    }
  }, []);

  const fetchHistorical = useCallback((appName: string, service: string, limit = 25) => {
    if (!wsRef.current?.isConnected()) return;
    const key = historicalKey(appName, service);
    historicalChunksRef.current.delete(key);
    setHistorical(prev => {
      const next = new Map(prev);
      next.set(key, { records: prev.get(key)?.records || [], loading: true });
      return next;
    });
    wsRef.current.sendMessage({
      action: 'getHistorical',
      app_name: appName,
      service,
      limit,
    });
  }, []);

  const fetchLogs = useCallback(
    (deploymentId: string, opts?: FetchLogsOptions): Promise<DeploymentLogs> =>
      new Promise<DeploymentLogs>((resolve, reject) => {
        if (!deploymentId) {
          reject(new Error('Missing deployment id'));
          return;
        }
        if (!wsRef.current?.isConnected()) {
          reject(new Error('Not connected'));
          return;
        }
        const timer = setTimeout(() => {
          // Drop this specific waiter (by timer identity) and reject it. A
          // late response then simply finds no matching waiter and is ignored.
          const queue = logsPendingRef.current.get(deploymentId);
          if (queue) {
            const idx = queue.findIndex(p => p.timer === timer);
            if (idx >= 0) {
              queue.splice(idx, 1);
              if (queue.length === 0) logsPendingRef.current.delete(deploymentId);
            }
          }
          reject(new Error('Timed out fetching logs'));
        }, LOGS_TIMEOUT_MS);

        const queue = logsPendingRef.current.get(deploymentId) || [];
        queue.push({ resolve, reject, timer });
        logsPendingRef.current.set(deploymentId, queue);
        // No engine field: the backend reads the record's metadata.engine
        // itself and answers a non-Argo deployment with source "unsupported".
        wsRef.current.sendMessage({
          action: 'getLogs',
          deployment_id: deploymentId,
          ...(opts?.task ? { task: opts.task } : {}),
          ...(opts?.tail !== undefined ? { tail: opts.tail } : {}),
        });
      }),
    []
  );

  const startRca = useCallback(
    (deploymentId: string): Promise<RcaStartResult> =>
      new Promise<RcaStartResult>((resolve, reject) => {
        if (!deploymentId) {
          reject(new Error('Missing deployment id'));
          return;
        }
        if (!wsRef.current?.isConnected()) {
          reject(new Error('Not connected'));
          return;
        }
        if (rcaPendingRef.current.has(deploymentId)) {
          reject(new Error('A request to analyse this deployment is already in flight'));
          return;
        }
        const timer = setTimeout(() => {
          rcaPendingRef.current.delete(deploymentId);
          reject(new Error('Timed out starting the analysis'));
        }, RCA_TIMEOUT_MS);
        rcaPendingRef.current.set(deploymentId, { resolve, reject, timer });
        wsRef.current.sendMessage({ action: 'startRca', deployment_id: deploymentId });
      }),
    []
  );

  const submitRcaFeedback = useCallback(
    (input: RcaFeedbackInput): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        const { deploymentId } = input;
        if (!deploymentId) {
          reject(new Error('Missing deployment id'));
          return;
        }
        if (!wsRef.current?.isConnected()) {
          reject(new Error('Not connected'));
          return;
        }
        if (rcaFeedbackPendingRef.current.has(deploymentId)) {
          reject(new Error('Feedback for this deployment is already being saved'));
          return;
        }
        const timer = setTimeout(() => {
          rcaFeedbackPendingRef.current.delete(deploymentId);
          reject(new Error('Timed out saving the feedback'));
        }, RCA_TIMEOUT_MS);
        rcaFeedbackPendingRef.current.set(deploymentId, { resolve, reject, timer });
        wsRef.current.sendMessage(rcaFeedbackMessage(input));
      }),
    []
  );

  // A reply never reaches a replacement connection, so a drop rejects every
  // pending startRca and submitRcaFeedback at once instead of leaving the
  // control busy until its timeout. A retry is safe: the Lambda answers
  // already_running for a run the lost request did start, and a second verdict
  // simply overwrites the first.
  useEffect(() => {
    if (wsStatus === 'connected') return;
    [rcaPendingRef.current, rcaFeedbackPendingRef.current].forEach(pending => {
      pending.forEach(p => {
        clearTimeout(p.timer);
        p.reject(new Error('Connection lost; try again'));
      });
      pending.clear();
    });
  }, [wsStatus]);

  // Same for rollback profiles: a lost reply would otherwise leave a version
  // "loading" forever, and fetchRollbackProfileVersion would refuse to resend.
  useEffect(() => {
    if (wsStatus === 'connected') return;
    rollbackProfileChunksRef.current.clear();
    setRollbackProfiles(prev => {
      let changed = false;
      const next = new Map(prev);
      prev.forEach((slice, key) => {
        const versions = Object.fromEntries(
          Object.entries(slice.versions).filter(([, v]) => !v.loading || v.entry)
        );
        if (slice.loading || Object.keys(versions).length !== Object.keys(slice.versions).length) {
          changed = true;
          next.set(key, { ...slice, loading: false, versions });
        }
      });
      return changed ? next : prev;
    });
  }, [wsStatus]);

  // A reply lost with the connection would leave the section loading forever.
  useEffect(() => {
    if (wsStatus === 'connected') return;
    moduleVersionChunksRef.current.clear();
    settleModuleVersions(0);
    stackModuleChunksRef.current.clear();
    setStackModules(prev => {
      if (!Array.from(prev.values()).some(s => s.loading)) return prev;
      const next = new Map(prev);
      prev.forEach((slice, key) => {
        if (slice.loading) next.set(key, { ...slice, loading: false });
      });
      return next;
    });
  }, [wsStatus, settleModuleVersions]);

  // Lost replies would leave a tab or the doc drawer loading forever, and a
  // doc stuck loading would never be resent.
  useEffect(() => {
    if (wsStatus === 'connected') return;
    rollbackListChunksRef.current.clear();
    rollbackDocChunksRef.current.clear();
    const settleLists = <T,>(prev: Map<string, RollbackListSlice<T>>) => {
      if (!Array.from(prev.values()).some(s => s.loading)) return prev;
      const next = new Map(prev);
      prev.forEach((slice, key) => {
        if (slice.loading) next.set(key, { ...slice, loading: false });
      });
      return next;
    };
    setRollbackWatches(settleLists);
    setRollbackBreaches(settleLists);
    setRollbackDocs(prev => {
      if (!Array.from(prev.values()).some(d => d.loading)) return prev;
      const next = new Map(prev);
      prev.forEach((d, key) => {
        if (d.loading) next.delete(key);
      });
      return next;
    });
  }, [wsStatus]);

  // Reject any outstanding RCA requests and clear their timers on unmount.
  useEffect(() => {
    const pendings = [rcaPendingRef.current, rcaFeedbackPendingRef.current];
    return () => {
      pendings.forEach(pending => {
        pending.forEach(p => {
          clearTimeout(p.timer);
          p.reject(new Error('Cancelled'));
        });
        pending.clear();
      });
    };
  }, []);

  // Reject any outstanding log requests and clear their timers on unmount.
  useEffect(() => {
    const pending = logsPendingRef.current;
    return () => {
      pending.forEach(queue =>
        queue.forEach(p => {
          clearTimeout(p.timer);
          p.reject(new Error('Cancelled'));
        })
      );
      pending.clear();
    };
  }, []);

  const fetchJobs = useCallback(() => {
    if (!wsRef.current?.isConnected()) return;
    setJobsLoading(true);
    jobsChunksRef.current = [];
    wsRef.current.sendMessage({ action: 'getJobs' });
  }, []);

  const fetchRollbackProfiles = useCallback((appName: string, jobId: string) => {
    if (!wsRef.current?.isConnected()) return;
    const key = rollbackProfilesKey(appName, jobId);
    setRollbackProfiles(prev => {
      const next = new Map(prev);
      const cur = prev.get(key);
      next.set(key, {
        entries: cur?.entries || [],
        loading: true,
        loaded: cur?.loaded || false,
        versions: cur?.versions || {},
      });
      return next;
    });
    wsRef.current.sendMessage({ action: 'getRollbackProfiles', app_name: appName, job_id: jobId });
  }, []);

  const fetchRollbackProfileVersion = useCallback(
    (appName: string, jobId: string, envRegion: string, version: number) => {
      if (!wsRef.current?.isConnected()) return;
      const key = rollbackProfilesKey(appName, jobId);
      const vKey = profileVersionKey(envRegion, version);
      const held = rollbackProfilesRef.current.get(key)?.versions[vKey];
      if (held && (held.entry || held.loading)) return;
      setRollbackProfiles(prev => {
        const next = new Map(prev);
        const cur = prev.get(key) || { entries: [], loading: false, loaded: false, versions: {} };
        next.set(key, { ...cur, versions: { ...cur.versions, [vKey]: { loading: true } } });
        return next;
      });
      wsRef.current.sendMessage({
        action: 'getRollbackProfiles',
        app_name: appName,
        job_id: jobId,
        env_region: envRegion,
        version,
      });
    },
    []
  );

  const fetchStackModules = useCallback((appName: string, jobId: string) => {
    if (!wsRef.current?.isConnected()) return;
    const key = stackModulesKey(appName, jobId);
    setStackModules(prev => {
      const next = new Map(prev);
      const cur = prev.get(key);
      next.set(key, { stacks: cur?.stacks || [], loading: true, loaded: cur?.loaded || false });
      return next;
    });
    wsRef.current.sendMessage({ action: 'getStackModules', app_name: appName, job_id: jobId });
  }, []);

  // A request with no reply in this long gives up, e.g. a backend without the
  // getModuleVersions route, so the page falls back instead of waiting forever.
  const moduleVersionsTimerRef = useRef<number | undefined>(undefined);
  const fetchModuleVersions = useCallback((sources: string[]) => {
    if (!wsRef.current?.isConnected() || sources.length === 0) return;
    settleModuleVersions(moduleVersionsPendingRef.current + 1);
    wsRef.current.sendMessage({ action: 'getModuleVersions', sources });
    window.clearTimeout(moduleVersionsTimerRef.current);
    moduleVersionsTimerRef.current = window.setTimeout(() => {
      if (moduleVersionsPendingRef.current === 0) return;
      settleModuleVersions(0);
      setModuleVersionsError('no reply from the registry lookup');
    }, MODULE_VERSIONS_TIMEOUT_MS);
  }, [settleModuleVersions]);

  const fetchRollbackList = useCallback(
    (
      action: 'getRollbackWatches' | 'getRollbackBreaches',
      appName: string,
      jobId: string,
      opts: RollbackListOptions = {}
    ) => {
      if (!wsRef.current?.isConnected()) return;
      const setList: React.Dispatch<React.SetStateAction<Map<string, RollbackListSlice<any>>>> =
        action === 'getRollbackWatches' ? setRollbackWatches : setRollbackBreaches;
      const key = rollbackListKey(appName, jobId, opts.envRegion);
      setList(prev => {
        const next = new Map(prev);
        const cur = prev.get(key);
        next.set(key, {
          items: cur?.items || [],
          loading: true,
          loaded: cur?.loaded || false,
          nextBefore: cur?.nextBefore,
        });
        return next;
      });
      wsRef.current.sendMessage({
        action,
        app_name: appName,
        job_id: jobId,
        ...(opts.envRegion ? { env_region: opts.envRegion } : {}),
        ...(opts.before ? { before: opts.before } : {}),
        ...(action === 'getRollbackWatches' ? { limit: WATCHES_PAGE_SIZE } : {}),
      });
    },
    []
  );

  const fetchRollbackWatches = useCallback(
    (appName: string, jobId: string, opts?: RollbackListOptions) =>
      fetchRollbackList('getRollbackWatches', appName, jobId, opts),
    [fetchRollbackList]
  );

  const fetchRollbackBreaches = useCallback(
    (appName: string, jobId: string, opts?: RollbackListOptions) =>
      fetchRollbackList('getRollbackBreaches', appName, jobId, opts),
    [fetchRollbackList]
  );

  const fetchRollbackDoc = useCallback((appName: string, jobId: string, kind: RollbackDocKind, key: string) => {
    if (!wsRef.current?.isConnected()) return;
    const held = rollbackDocsRef.current.get(key);
    if (held && (held.doc || held.loading)) return;
    setRollbackDocs(prev => {
      const next = new Map(prev);
      next.set(key, { loading: true });
      return next;
    });
    wsRef.current.sendMessage({ action: 'getRollbackDoc', app_name: appName, job_id: jobId, kind, key });
  }, []);

  const fetchEnvironmentInfo = useCallback(() => {
    if (environmentInfoLoaded || environmentInfoRequestedRef.current) return;
    if (!wsRef.current?.isConnected()) return;
    environmentInfoRequestedRef.current = true;
    wsRef.current.sendMessage({ action: 'getEnvironmentInfo' });
  }, [environmentInfoLoaded]);

  // Queue a record's artifact lookup (deduped). Drives both the github button
  // cache and the explicit "Artifact info" panel.
  const fetchArtifactFor = useCallback(
    (record: DeploymentRecord) => {
      if (!buildArtifactMessage(record)) return; // missing app/job
      const key = artifactKey(record);
      // Already resolved, in flight, or queued → nothing to do.
      if (
        artifactInFlightRef.current === key ||
        artifactQueueRef.current.includes(key)
      ) {
        return;
      }
      let already = false;
      setArtifactCache(prev => {
        already = prev.has(key);
        return prev;
      });
      if (already) return;
      artifactRecordsRef.current.set(key, record);
      artifactQueueRef.current.push(key);
      setArtifactLoadingKeys(prev => new Set(prev).add(key));
      // Clear any prior timeout/error state — this is a fresh attempt.
      setArtifactErrorKeys(prev => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      pumpArtifacts();
    },
    [pumpArtifacts]
  );

  const githubLinkFor = useCallback(
    (record: DeploymentRecord) => artifactCache.get(artifactKey(record))?.github_link,
    [artifactCache]
  );

  const artifactStatusFor = useCallback(
    (record: DeploymentRecord): ArtifactStatus => {
      const key = artifactKey(record);
      if (artifactCache.has(key)) return artifactCache.get(key)?.github_link ? 'ready' : 'none';
      if (artifactLoadingKeys.has(key)) return 'loading';
      if (artifactErrorKeys.has(key)) return 'error';
      return 'idle';
    },
    [artifactCache, artifactLoadingKeys, artifactErrorKeys]
  );

  const clearError = useCallback(() => setError(null), []);

  // ---- Derived slices ----

  // The single point where a stray-named job (e.g. a `static` job whose
  // release package never embeds its app_name, unlike every other type)
  // gets folded onto its owning app's card. Every consumer below — and every
  // page that reads `records` from this hook — sees the corrected `app`
  // field for free; nothing downstream needs its own fix.
  const records = useMemo(() => foldStrayAppsIntoNamesakes(rawRecords), [rawRecords]);

  const planLinks = useMemo(() => {
    const latest = new Map<string, PlanLinkInfo>();
    records.forEach(r => {
      if (!r.s3_plan_link || !r.app) return;
      const jobId = r.serviceName || r.job_id || '';
      if (!jobId) return;
      const key = planLinkKey(r.app, jobId, r.type || '', r.envName || r.env || '');
      const ts = new Date(r.timestamp).getTime();
      const cur = latest.get(key);
      if (!cur || ts > cur.ts) latest.set(key, { link: r.s3_plan_link, ts });
    });
    return latest;
  }, [records]);

  const staticRecords = useMemo(
    () => records.filter(r => r.ttl === undefined || r.ttl === null),
    [records]
  );

  const dynamicRecords = useMemo(
    () => records.filter(r => r.ttl !== undefined && r.ttl !== null),
    [records]
  );

  // Stale/superseded failed & stuck-deploying records to hide from current-state
  // surfaces. Computed over the full live list so cross-env supersedes are seen.
  const staleIds = useMemo(() => staleCurrentIds(records), [records]);
  const currentRecords = useMemo(
    () => (staleIds.size === 0 ? staticRecords : staticRecords.filter(r => !r.id || !staleIds.has(r.id))),
    [staticRecords, staleIds]
  );

  const uniques = useMemo(() => {
    const apps = new Set<string>();
    const services = new Set<string>();
    const envs = new Set<string>();
    const types = new Set<string>();
    const users = new Set<string>();
    staticRecords.forEach(r => {
      if (r.app) apps.add(r.app);
      // Type-qualified so same-named jobs of different types (an `ecs`
      // deploy vs. a `terraform` plan both named "ecs") are independently
      // selectable in every filter UI that reads uniqueServices, rather
      // than a bare name matching both at once.
      if (r.serviceName) services.add(serviceFilterKey(r.type, r.serviceName));
      if (r.envName) envs.add(r.envName);
      if (r.type) types.add(r.type);
      if (r.buildUser) users.add(r.buildUser);
    });
    const sortedEnvs = (() => {
      const arr = Array.from(envs);
      const priority = ['dev', 'prod'].filter(e => arr.includes(e));
      const rest = arr.filter(e => !priority.includes(e)).sort();
      return [...priority, ...rest];
    })();
    return {
      uniqueApps: Array.from(apps).sort(),
      uniqueServices: Array.from(services).sort(),
      uniqueEnvs: sortedEnvs,
      uniqueTypes: Array.from(types).sort(),
      uniqueBuildUsers: Array.from(users).sort(),
    };
  }, [staticRecords]);

  const value = useMemo<DeploymentDataValue>(
    () => ({
      records,
      staticRecords,
      dynamicRecords,
      currentRecords,
      staleCurrentIds: staleIds,
      wsStatus,
      loading,
      loadingProgress,
      error,
      clearError,
      reconnect,
      refresh,
      fetchHistorical,
      historical,
      fetchLogs,
      startRca,
      submitRcaFeedback,
      rollbackProfiles,
      fetchRollbackProfiles,
      fetchRollbackProfileVersion,
      stackModules,
      fetchStackModules,
      moduleVersions,
      moduleVersionsPending,
      moduleVersionsError,
      fetchModuleVersions,
      rollbackWatches,
      fetchRollbackWatches,
      rollbackBreaches,
      fetchRollbackBreaches,
      rollbackDocs,
      fetchRollbackDoc,
      jobs,
      jobsLoading,
      jobsLoaded,
      fetchJobs,
      environmentInfo,
      environmentInfoLoaded,
      fetchEnvironmentInfo,
      fetchArtifactFor,
      githubLinkFor,
      artifactStatusFor,
      ...uniques,
    }),
    [
      records,
      staticRecords,
      dynamicRecords,
      currentRecords,
      staleIds,
      wsStatus,
      loading,
      loadingProgress,
      error,
      clearError,
      reconnect,
      refresh,
      fetchHistorical,
      historical,
      fetchLogs,
      startRca,
      submitRcaFeedback,
      rollbackProfiles,
      fetchRollbackProfiles,
      fetchRollbackProfileVersion,
      stackModules,
      fetchStackModules,
      moduleVersions,
      moduleVersionsPending,
      moduleVersionsError,
      fetchModuleVersions,
      rollbackWatches,
      fetchRollbackWatches,
      rollbackBreaches,
      fetchRollbackBreaches,
      rollbackDocs,
      fetchRollbackDoc,
      jobs,
      jobsLoading,
      jobsLoaded,
      fetchJobs,
      environmentInfo,
      environmentInfoLoaded,
      fetchEnvironmentInfo,
      fetchArtifactFor,
      githubLinkFor,
      artifactStatusFor,
      uniques,
    ]
  );

  return (
    <DeploymentDataContext.Provider value={value}>
      <LocksContext.Provider value={locks}>
        <PlanLinksContext.Provider value={planLinks}>
          <FlashedKeysContext.Provider value={flashedKeys}>{children}</FlashedKeysContext.Provider>
        </PlanLinksContext.Provider>
      </LocksContext.Provider>
    </DeploymentDataContext.Provider>
  );
};
